import { useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Mail, MessageCircle, Send, Loader2 } from 'lucide-react';
import GlowBackground from '@/components/site/GlowBackground';
import SectionHeader from '@/components/site/SectionHeader';
import { BRAND, whatsappLink } from '@/lib/constants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [status, setStatus] = useState('idle');

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || (!form.email.trim() && !form.phone.trim()) || !form.message.trim()) {
      toast.error('Please fill in your name, message and at least one contact detail.');
      return;
    }
    setStatus('sending');
    try {
      await axios.post(`${API}/contact`, { ...form, website: '' });
      setStatus('sent');
      toast.success("Thanks — we'll be in touch soon.");
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      console.error(err);
      toast.error('Could not send your message. Please try WhatsApp instead.');
      setStatus('idle');
    }
  };

  return (
    <div className="relative pt-28 md:pt-36 pb-16" data-testid="contact-page">
      <div className="absolute inset-0 pointer-events-none"><GlowBackground /></div>
      <div className="relative mx-auto max-w-6xl px-5 md:px-8">
        <SectionHeader
          eyebrow="Get in touch"
          title={<>Talk to <span className="text-[#FFD400]">us.</span></>}
          subtitle="Prefer to skip the form? WhatsApp is the fastest way. Otherwise send a message and we will reply as soon as possible."
        />

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <motion.form
            onSubmit={onSubmit}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7 rounded-3xl border border-white/10 bg-[#0d1826]/60 p-6 md:p-10"
            data-testid="contact-form"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="text-[10px] font-inter font-bold uppercase tracking-[0.28em] text-white/50">Name</label>
                <input
                  data-testid="contact-name"
                  value={form.name} onChange={(e) => setField('name', e.target.value)}
                  className="mt-2 w-full min-h-[56px] bg-[#121212] border border-white/10 rounded-xl px-4 text-white font-inter focus:border-[#39A8FF] focus:outline-none"
                  placeholder="Full name" required
                />
              </div>
              <div>
                <label className="text-[10px] font-inter font-bold uppercase tracking-[0.28em] text-white/50">Email</label>
                <input
                  type="email" data-testid="contact-email"
                  value={form.email} onChange={(e) => setField('email', e.target.value)}
                  className="mt-2 w-full min-h-[56px] bg-[#121212] border border-white/10 rounded-xl px-4 text-white font-inter focus:border-[#39A8FF] focus:outline-none"
                  placeholder="you@example.co.za"
                />
              </div>
              <div>
                <label className="text-[10px] font-inter font-bold uppercase tracking-[0.28em] text-white/50">Phone</label>
                <input
                  type="tel" data-testid="contact-phone"
                  value={form.phone} onChange={(e) => setField('phone', e.target.value)}
                  className="mt-2 w-full min-h-[56px] bg-[#121212] border border-white/10 rounded-xl px-4 text-white font-inter focus:border-[#39A8FF] focus:outline-none"
                  placeholder="+27 …"
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] font-inter font-bold uppercase tracking-[0.28em] text-white/50">Message</label>
                <textarea
                  rows={6} data-testid="contact-message"
                  value={form.message} onChange={(e) => setField('message', e.target.value)}
                  className="mt-2 w-full bg-[#121212] border border-white/10 rounded-xl p-4 text-white font-inter focus:border-[#39A8FF] focus:outline-none resize-none"
                  placeholder="How can we help?" required
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={status === 'sending'}
              data-testid="contact-submit"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#FFD400] px-7 py-4 text-sm font-poppins font-bold uppercase tracking-wide text-[#07111F] disabled:opacity-50"
            >
              {status === 'sending' ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : <><Send className="h-4 w-4" /> Send Message</>}
            </button>
            {status === 'sent' && (
              <p className="mt-4 font-inter text-sm text-[#39A8FF]">Thanks — your message has been received.</p>
            )}
          </motion.form>

          <div className="lg:col-span-5 flex flex-col gap-4">
            <div className="rounded-3xl border border-white/10 bg-[#0d1826]/60 p-8">
              <div className="text-[10px] font-inter font-bold uppercase tracking-[0.28em] text-[#FFD400]">Direct</div>
              <ul className="mt-6 space-y-4 font-inter text-white/80">
                <li className="flex items-start gap-3"><Mail className="h-5 w-5 mt-0.5 text-[#39A8FF]" /><a data-testid="contact-email-link" className="hover:text-white break-all" href={`mailto:${BRAND.email}`}>{BRAND.email}</a></li>
              </ul>
              <a
                href={whatsappLink()}
                target="_blank"
                rel="noreferrer noopener"
                data-testid="contact-whatsapp"
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366]/10 border border-[#25D366]/30 px-6 py-4 text-sm font-poppins font-bold uppercase tracking-wide text-white hover:bg-[#25D366]/20 transition-colors"
              >
                <MessageCircle className="h-4 w-4 text-[#25D366]" /> Chat on WhatsApp
              </a>
            </div>

            <div className="rounded-3xl border border-white/10 bg-[#0d1826]/60 p-8">
              <div className="text-[10px] font-inter font-bold uppercase tracking-[0.28em] text-[#FFD400]">Coverage</div>
              <div className="mt-3 font-poppins font-semibold text-white text-lg">Serving clients across South Africa.</div>
              <p className="mt-3 font-inter text-white/60 text-sm leading-relaxed">
                We accept enquiries from all nine provinces. WhatsApp is available 24/7 — we respond within business hours.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
